import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, getUser } from "@/lib/api";
import { Trophy, Flame, Star, Award, Medal } from "lucide-react";

export default function StudentAchievements() {
  const [streak, setStreak] = useState(null);
  const [board, setBoard] = useState([]);
  const me = getUser();

  useEffect(() => {
    (async () => {
      try {
        const [s, l] = await Promise.all([api.get("/me/streak"), api.get("/leaderboard")]);
        setStreak(s.data); setBoard(l.data);
      } catch { /* ignore */ }
    })();
  }, []);

  const unlocked = new Set((streak?.achievements || []).map((a) => a.key));

  return (
    <Layout title="Streak & Achievements" subtitle="Student · Progress">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-1 bg-gradient-to-br from-orange-500 to-pink-500 text-white rounded-2xl p-6" data-testid="streak-card">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] opacity-90"><Flame className="w-3.5 h-3.5" /> Daily streak</div>
          <div className="mt-4 font-display font-black text-6xl">{streak?.streak_days ?? 0}</div>
          <div className="text-sm opacity-90 mt-1">consecutive days</div>
          <div className="mt-4 text-xs opacity-75">{streak?.last_login_date ? `Last: ${streak.last_login_date}` : "Log in tomorrow to keep the streak!"}</div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-slate-500"><Award className="w-3.5 h-3.5" /> Achievements</div>
          <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Badges unlocked</h2>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            {(streak?.all_achievements || []).map((a) => {
              const ok = unlocked.has(a.key);
              return (
                <div key={a.key} data-testid={`ach-${a.key}`} className={`border rounded-xl p-3 flex items-start gap-2 ${ok ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50/50 opacity-60"}`}>
                  <div className="text-2xl">{a.emoji || "🏅"}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{a.title}</div>
                    <div className="text-xs text-slate-500">{a.desc}</div>
                    {ok && <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-amber-600 font-bold">Unlocked</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-slate-500"><Trophy className="w-3.5 h-3.5" /> Global</div>
        <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Learning Leaderboard</h2>
        <div className="mt-4 space-y-2" data-testid="leaderboard-list">
          {board.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No students yet — be the first!</div>}
          {board.map((s, i) => {
            const isMe = s.id === me?.id;
            const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
            return (
              <div key={s.id} data-testid={`lb-row-${i}`} className={`flex items-center gap-3 p-3 rounded-xl ${isMe ? "bg-blue-50 border border-blue-200" : "border border-slate-100"}`}>
                <div className="w-8 text-center font-bold text-slate-700">{medal || `#${i + 1}`}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 line-clamp-1">{s.name} {isMe && <span className="text-blue-600 text-xs">(you)</span>}</div>
                  <div className="text-[10px] text-slate-500">
                    🔥 {s.streak_days} · 🎓 {s.enrollments} · 💻 {s.coding_solved} · 🏅 {s.achievements.length}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-black text-lg text-slate-900">{s.score}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">pts</div>
                </div>
                {i < 3 && <Star className="w-4 h-4 text-amber-500" />}
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
